import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Table, Guest, TableAssignment } from '../types';
import { filterVisibleInGuests } from '../lib/guests/capacity';
import { Modal } from '../components/Modal';
import { Toast, ToastMessage } from '../components/Toast';
import { EmptyState } from '../components/EmptyState';
import * as Lucide from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PlusCircle } from 'lucide-react';

export default function SeatingPage() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [tables, setTables] = useState<Table[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [assignments, setAssignments] = useState<TableAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Table>>({ capacity: 10 });
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchData = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const [tRes, gRes, aRes] = await Promise.all([
      supabase.from('tables').select('*').eq('wedding_id', wedding.id).order('name', { ascending: true }),
      supabase.from('guests').select('*').eq('wedding_id', wedding.id).eq('rsvp_status', 'confirmed'),
      supabase.from('table_assignments').select('*').eq('wedding_id', wedding.id)
    ]);
    
    if (tRes.data) setTables(tRes.data);
    if (gRes.data) setGuests(filterVisibleInGuests(gRes.data));
    if (aRes.data) setAssignments(aRes.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [wedding]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('tables').insert({ ...formData, wedding_id: wedding.id });
    if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    setFormData({ capacity: 10 });
    fetchData();
  };

  const assignGuest = async (tableId: string, guestId: string) => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    await supabase.from('table_assignments').delete().eq('wedding_id', wedding.id).eq('guest_id', guestId);
    if (tableId) {
      const { error } = await supabase.from('table_assignments').insert({
        wedding_id: wedding.id,
        table_id: tableId,
        guest_id: guestId
      });
      if (error) showToast('error', `Chyba: ${error.message}`);
    }
    fetchData();
  };

  const deleteTable = async (id: string) => {
    if (!confirm(t('common.confirm_delete'))) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('tables').delete().eq('id', id);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchData();
  };

  const getGuestsAtTable = (tableId: string) => {
    const guestIds = assignments.filter(a => a.table_id === tableId).map(a => a.guest_id);
    return guests.filter(g => guestIds.includes(g.id));
  };

  const unassignedGuests = guests.filter(g => !assignments.some(a => a.guest_id === g.id));

  if (!wedding) return null;

  return (
    <div className="space-y-10 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.seating')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">Rozmístěte své hosty ke stolům přetažením nebo výběrem.</p>
        </div>
        <Button 
          onClick={() => setIsModalOpen(true)}
          variant="secondary"
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-12">
        {/* Unassigned Guests */}
        <div className="xl:col-span-1 space-y-6">
           <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-ds-border">
              <h2 className="text-ds-meta text-slate-400 mb-6 flex items-center gap-2">
                 <Lucide.Users size={14} className="text-brand-400" /> Hosté k usazení ({unassignedGuests.length})
              </h2>
              <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                 {unassignedGuests.map(g => (
                   <div key={g.id} className="p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800 text-sm text-slate-700 dark:text-slate-300">
                      {g.full_name}
                   </div>
                 ))}
                 {unassignedGuests.length === 0 && (
                    <div className="py-6">
                      <EmptyState 
                        icon={Lucide.Check} 
                        title="Vše hotovo"
                        description="Všichni potvrzení hosté jsou usazeni!"
                      />
                    </div>
                 )}
              </div>
           </div>
        </div>

        {/* Tables Grid */}
        <div className="xl:col-span-3">
           {tables.length === 0 ? (
             <EmptyState 
               icon={Lucide.LayoutGrid}
               title="Zatím žádná data"
               description="Dnes není uveden žádný stůl nebo nejsou dostupné žádné informace."
               action={{
                 label: "Vytvořit stůl",
                 onClick: () => setIsModalOpen(true),
                 icon: Lucide.Plus
               }}
             />
           ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               {tables.map(table => {
                 const tableGuests = getGuestsAtTable(table.id);
                 return (
                   <div key={table.id} className="bg-white dark:bg-slate-900 p-10 rounded-2xl border border-ds-border transition-all group relative">
                      <div className="flex justify-between items-start mb-8">
                         <div>
                            <h3 className="text-ds-h3 font-semibold text-slate-900 dark:text-white">{table.name}</h3>
                            <p className="text-ds-meta text-slate-400 mt-1">Kapacita: {table.capacity} osob</p>
                         </div>
                         <span className={`px-3 py-1 rounded-full text-ds-meta ${
                           tableGuests.length > table.capacity ? 'bg-red-50 text-red-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'
                         }`}>
                            {tableGuests.length} / {table.capacity}
                         </span>
                      </div>

                      <div className="space-y-3 min-h-[150px] bg-slate-50 dark:bg-slate-950/40 p-6 rounded-[2rem] border border-slate-50 dark:border-slate-800/50">
                         {tableGuests.map(g => (
                           <div key={g.id} className="flex justify-between items-center text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 p-3 px-4 rounded-xl border border-ds-border">
                              <span>{g.full_name}</span>
                              <button onClick={() => assignGuest('', g.id)} className="text-slate-200 hover:text-red-500 transition-colors">
                                 <Lucide.XCircle size={16} />
                              </button>
                           </div>
                         ))}
                         {tableGuests.length === 0 && <p className="text-xs text-slate-300 dark:text-slate-700 italic text-center py-8">Tento stůl je zatím prázdný.</p>}
                      </div>

                      <div className="mt-8 flex items-center gap-4">
                         <select 
                           value="" 
                           onChange={(e) => assignGuest(table.id, e.target.value)}
                           className="flex-1 bg-slate-100 dark:bg-slate-800 border-none rounded-xl px-4 py-3 text-xs text-slate-500 outline-none cursor-pointer"
                         >
                            <option value="">Přidat hosta ke stolu...</option>
                            {unassignedGuests.map(g => <option key={g.id} value={g.id}>{g.full_name}</option>)}
                         </select>
                         <button 
                            onClick={() => deleteTable(table.id)} 
                            className="text-slate-200 hover:text-red-500 transition-all p-2"
                         >
                            <Lucide.Trash2 size={20} />
                         </button>
                      </div>
                   </div>
                 );
               })}
             </div>
           )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Nový stůl"
      >
        <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-6">
              <div>
                <label className="text-ds-meta text-slate-500 block mb-2">Název stolu</label>
                <input 
                  type="text" 
                  required 
                  autoFocus
                  value={formData.name || ''} 
                  onChange={e => setFormData({...formData, name: e.target.value})} 
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none transition-all" 
                  placeholder="Např. Stůl pro rodinu" 
                />
              </div>
              <div>
                <label className="text-ds-meta text-slate-500 block mb-2">Kapacita (osob)</label>
                <input 
                  type="number" 
                  required
                  value={formData.capacity} 
                  onChange={e => setFormData({...formData, capacity: parseInt(e.target.value)})} 
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none transition-all" 
                />
              </div>
            </div>
            <button 
              type="submit" 
              className="w-full bg-brand-600 hover:bg-brand-700 text-white py-5 rounded-[2rem] font-bold text-lg transition-all shadow-xl shadow-brand-500/30 flex items-center justify-center gap-3"
            >
              <Lucide.Check size={22} /> Vytvořit stůl
            </button>
        </form>
      </Modal>

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}